import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, EyeOff, ExternalLink, ChevronDown, MessageSquare, CornerDownRight } from 'lucide-react';
import type { IaAlerta, IaAlertaSeveridade } from '../../../types';
import { ROTULO_MODULO, ROTULO_SEVERIDADE } from '../../hooks/useIaAlertas';
import { destinoDoAlerta } from '../../lib/vickAlertaDestino';

/**
 * A gravidade aparece em três canais ao mesmo tempo: cor da barra lateral,
 * rótulo escrito e ordem na fila. Cor sozinha não serve — quem não distingue
 * vermelho de âmbar ainda precisa saber o que é urgente, e o alerta continua
 * legível impresso ou em preto e branco.
 */
const CORES: Record<IaAlertaSeveridade, { borda: string; chip: string }> = {
  alta: {
    borda: 'border-l-red-500',
    chip: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  },
  media: {
    borda: 'border-l-amber-500',
    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
  baixa: {
    borda: 'border-l-slate-400',
    chip: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  },
};

const fmt = (v: unknown): string => {
  if (v == null) return '—';
  if (typeof v === 'number') return v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (Array.isArray(v) || typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/**
 * Chaves de `dados` que guardam a LISTA de quem foi afetado.
 *
 * `mais_criticas` e `maiores_desvios` caíam no `fmt()` genérico e saíam como
 * `[{"uc":"6924501204","clien…` no meio da grade de números — o alerta dizia
 * "229 UCs" e a única resposta para "quais?" era um JSON truncado. Agora saem
 * em lista, abaixo dos números.
 */
const CHAVES_LISTA = ['mais_criticas', 'sem_injecao_prolongada', 'maiores', 'maiores_desvios', 'exemplos'] as const;

const TITULO_LISTA: Record<string, string> = {
  mais_criticas: 'As mais críticas',
  sem_injecao_prolongada: 'Nunca receberam injeção',
  maiores: 'Os maiores',
  maiores_desvios: 'Os maiores desvios',
  exemplos: 'Alguns exemplos',
};

/** Campos internos e listas longas ficam fora da grade de números. */
const oculto = (k: string) =>
  k.startsWith('_') || k === 'meses_comparados' || (CHAVES_LISTA as readonly string[]).includes(k);

/**
 * Rótulo legível para uma chave de `dados`.
 *
 * As chaves são snake_case sem acento porque vêm de JSON gravado pelo vigia.
 * Trocar underscore por espaço resolve a maioria, mas deixa "Dias ate vencer"
 * na tela — português errado numa tela que a diretoria olha. O mapa cobre as
 * que aparecem de fato; o resto cai no genérico.
 */
const ROTULOS: Record<string, string> = {
  uc: 'UC',
  mes: 'Mês',
  dias_ate_vencer: 'Dias até vencer',
  limiar_dias: 'Limiar (dias)',
  status_da_fatura: 'Status da fatura',
  cobertura_meses: 'Cobertura (meses)',
  saldo_kwh: 'Saldo (kWh)',
  consumo_base_kwh: 'Consumo base (kWh)',
  consumo_do_mes_kwh: 'Consumo do mês (kWh)',
  media_cadastrada_kwh: 'Média cadastrada (kWh)',
  variacao_percent: 'Variação (%)',
  limiar_percent: 'Limiar (%)',
  minimo_saudavel_meses: 'Mínimo saudável (meses)',
  limiar_meses: 'Limiar (meses)',
  sem_nenhum_saldo: 'Sem nenhum saldo',
  sem_injecao_apos_prazo: 'Sem injeção após 3 meses',
  aguardando_primeira_injecao: 'Aguardando 1ª injeção (fora do alerta)',
  meses_de_fatura_sem_credito: 'Meses de fatura sem crédito',
  total_em_aberto: 'Total em aberto',
  horas_sem_leitura: 'Horas sem leitura',
  ultima_leitura: 'Última leitura',
  ultima_geracao: 'Última geração',
  taxa_do_mes_percent: 'Taxa do mês (%)',
  media_3_meses_percent: 'Média de 3 meses (%)',
  queda_em_pontos: 'Queda (pontos)',
  limiar_pontos: 'Limiar (pontos)',
  emitido_no_mes: 'Emitido no mês',
  recebido_no_mes: 'Recebido no mês',
  consumo_instavel: 'Consumo instável',
  mes_da_fatura: 'Mês da fatura',
  qtd: 'Faturas em aberto',
  valor: 'Valor',
  direcao: 'Direção',
};

const rotulo = (k: string) =>
  ROTULOS[k] ?? k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** Quem a linha da lista descreve: a UC, o cliente, ou os dois. */
const identificar = (item: Record<string, any>): string => {
  const uc = item?.uc != null ? `UC ${item.uc}` : null;
  const cliente = item?.cliente != null ? String(item.cliente) : null;
  return [uc, cliente].filter(Boolean).join(' · ') || '—';
};

/**
 * Quem foi afetado, em lista.
 *
 * O vigia guarda no máximo 10 — o alerta diz o total, esta lista mostra a ponta
 * mais grave. Não substitui a tela de destino: existe para a pessoa decidir se
 * vale abrir agora, sem ter que sair da fila para descobrir do que se trata.
 */
const ListaAfetados: React.FC<{ chave: string; itens: any[] }> = ({ chave, itens }) => (
  <div className="mt-2.5 border-t border-gray-100 dark:border-slate-700 pt-2.5">
    <p className="text-[11px] font-semibold text-gray-600 dark:text-slate-300 mb-1.5">
      {TITULO_LISTA[chave] ?? rotulo(chave)}
      <span className="ml-1 font-normal text-gray-400">({itens.length})</span>
    </p>
    <ul className="space-y-1">
      {itens.map((item, i) => {
        const campos = Object.entries(item ?? {}).filter(([k]) => k !== 'uc' && k !== 'cliente');
        return (
          <li key={i} className="text-[11px] leading-snug border-l-2 border-gray-200 dark:border-slate-600 pl-2">
            <span className="font-medium text-gray-700 dark:text-slate-200 break-all">{identificar(item)}</span>
            {campos.length > 0 ? (
              <span className="text-gray-500 dark:text-slate-400">
                {' — '}
                {campos.map(([k, v]) => `${rotulo(k)}: ${fmt(v)}`).join(' · ')}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  </div>
);

interface Props {
  alerta: IaAlerta;
  /**
   * Ausente = card só informa (é o caso do painel flutuante). O que decide se a
   * triagem aparece é a existência do callback, não uma flag de modo: o painel
   * simplesmente não passa `onBaixar`.
   */
  onBaixar?: (id: number, estado: 'resolvido' | 'ignorado', motivo?: string) => Promise<string | null>;
  onPerguntar?: (alerta: IaAlerta) => void;
  /** Apareceu depois da última vez que esta pessoa leu a fila. */
  naoLido?: boolean;
  /** Chamado ao sair para a tela de destino (o painel flutuante usa para se fechar). */
  onNavegar?: () => void;
}

const AlertaCard: React.FC<Props> = ({ alerta, onBaixar, onPerguntar, naoLido = false, onNavegar }) => {
  const [aberto, setAberto] = useState(false);
  const [ignorando, setIgnorando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const cor = CORES[alerta.severidade];
  const dados = alerta.dados ?? {};
  const numeros = Object.entries(dados).filter(([k]) => !oculto(k));
  const listas = CHAVES_LISTA
    .map((k) => [k, (dados as any)[k]] as const)
    .filter(([, v]) => Array.isArray(v) && v.length > 0);
  const nota = (dados as any)?._nota as string | undefined;
  const destino = destinoDoAlerta(alerta);
  const temDetalhe = numeros.length > 0 || listas.length > 0 || !!destino?.ondeResolver;

  const baixar = async (estado: 'resolvido' | 'ignorado') => {
    if (!onBaixar) return;
    setSalvando(true);
    setErro(null);
    const msg = await onBaixar(alerta.id, estado, estado === 'ignorado' ? motivo : undefined);
    setSalvando(false);
    if (msg) setErro(msg);
  };

  /**
   * Sair para o destino.
   *
   * São dois tipos: página (react-router) e modal global — a Central de
   * Inadimplência não tem rota própria, abre por CustomEvent. O card não precisa
   * saber a diferença: `destinoDoAlerta` já decidiu qual é qual.
   */
  const abrirDestino = () => {
    if (destino?.evento) {
      window.dispatchEvent(new CustomEvent(destino.evento.nome, { detail: destino.evento.detalhe }));
    }
    onNavegar?.();
  };

  const botaoDestino = !destino ? null : destino.rota ? (
    <Link
      to={destino.rota}
      onClick={abrirDestino}
      className="inline-flex items-center gap-1 text-xs text-primary dark:text-primary-light hover:underline font-medium"
    >
      <ExternalLink size={12} /> {destino.rotulo}
    </Link>
  ) : (
    <button
      onClick={abrirDestino}
      className="inline-flex items-center gap-1 text-xs text-primary dark:text-primary-light hover:underline font-medium"
    >
      <ExternalLink size={12} /> {destino.rotulo}
    </button>
  );

  const temAcoes = !!botaoDestino || !!onPerguntar || !!onBaixar;

  return (
    <div
      className={`border-l-4 ${cor.borda} bg-white dark:bg-slate-800 rounded-r-xl border-y border-r border-gray-100 dark:border-slate-700 p-3 transition-shadow hover:shadow-sm`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-gray-400" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 dark:text-white leading-snug">{alerta.titulo}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {/* "Novo" é por pessoa (localStorage), não por linha da tabela: um
                alerta lido por quem cuida do Rateio continua novo para o
                Financeiro. Ver src/hooks/useAlertasLidos.ts. */}
            {naoLido ? (
              <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/15 text-primary dark:bg-primary-light/20 dark:text-primary-light">
                Novo
              </span>
            ) : null}
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${cor.chip}`}>
              {ROTULO_SEVERIDADE[alerta.severidade]}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-slate-400">{ROTULO_MODULO[alerta.modulo]}</span>
            <span className="text-[10px] text-gray-400 dark:text-slate-500">
              {new Date(alerta.detectado_em).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>
        </div>
        {temDetalhe ? (
          <button
            onClick={() => setAberto((v) => !v)}
            className="shrink-0 text-gray-400 hover:text-primary dark:hover:text-primary-light transition-colors p-1"
            title={aberto ? 'Esconder os detalhes' : 'Ver os números, quem foi afetado e onde se resolve'}
            aria-expanded={aberto}
          >
            <ChevronDown size={16} className={`transition-transform ${aberto ? 'rotate-180' : ''}`} />
          </button>
        ) : null}
      </div>

      {/* Os números que sustentam o alerta ficam a um clique. É o que separa um
          aviso de um boato: dá para conferir a conta sem confiar no texto. */}
      {aberto && numeros.length > 0 ? (
        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs border-t border-gray-100 dark:border-slate-700 pt-2.5">
          {numeros.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="text-gray-500 dark:text-slate-400 truncate">{rotulo(k)}</dt>
              <dd className="font-medium text-gray-800 dark:text-slate-200 truncate">{fmt(v)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {aberto ? listas.map(([k, v]) => <ListaAfetados key={k} chave={k} itens={v as any[]} />) : null}

      {/* A ressalva é o que impede um número honesto de ser lido errado. */}
      {aberto && nota ? (
        <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400 italic leading-snug">{nota}</p>
      ) : null}

      {/* Onde se resolve. A tela de destino não se explica sozinha: o Simulador
          de Rateio tem cinco abas e nenhuma se chama "saldo". Abrir a tela certa
          e largar a pessoa lá dentro resolve metade do problema. */}
      {aberto && destino?.ondeResolver ? (
        <p className="mt-2 flex gap-1.5 text-[11px] text-gray-600 dark:text-slate-300 leading-snug bg-gray-50 dark:bg-slate-900/50 rounded-lg px-2 py-1.5">
          <CornerDownRight size={12} className="mt-0.5 shrink-0 text-gray-400" />
          <span>{destino.ondeResolver}</span>
        </p>
      ) : null}

      {erro ? <p className="mt-2 text-xs text-red-600 dark:text-red-400">{erro}</p> : null}

      {!ignorando && temAcoes ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-gray-100 dark:border-slate-700 pt-2.5">
          {botaoDestino}
          {onPerguntar ? (
            <button
              onClick={() => onPerguntar(alerta)}
              className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-slate-300 hover:text-primary transition-colors"
            >
              <MessageSquare size={12} /> Perguntar à Vick
            </button>
          ) : null}
          {onBaixar ? (
            <div className="ml-auto flex items-center gap-1.5">
              <button
                onClick={() => baixar('resolvido')}
                disabled={salvando}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-primary/10 dark:bg-primary-light/15 text-primary dark:text-primary-light hover:bg-primary/20 dark:hover:bg-primary-light/25 transition-colors disabled:opacity-50"
              >
                <Check size={12} /> Resolvido
              </button>
              <button
                onClick={() => setIgnorando(true)}
                disabled={salvando}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                <EyeOff size={12} /> Ignorar
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Ignorar exige motivo: é o motivo que permite ajustar o limiar depois,
          em vez de a equipe simplesmente desligar o alerta e esquecer. */}
      {ignorando ? (
        <div className="mt-2.5 border-t border-gray-100 dark:border-slate-700 pt-2.5">
          <label htmlFor={`motivo-${alerta.id}`} className="block text-xs text-gray-600 dark:text-slate-300 mb-1">
            Por que ignorar? É isso que ajusta o alerta depois.
          </label>
          <input
            id={`motivo-${alerta.id}`}
            autoFocus
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="ex.: cliente já avisou que vai reduzir o consumo"
            className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center gap-1.5 mt-1.5">
            <button
              onClick={() => baixar('ignorado')}
              disabled={salvando || motivo.trim().length < 3}
              className="text-xs px-2 py-1 rounded-lg bg-primary text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirmar
            </button>
            <button
              onClick={() => { setIgnorando(false); setMotivo(''); setErro(null); }}
              className="text-xs px-2 py-1 rounded-lg text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AlertaCard;
